/* Store locator (sections/store-locator.liquid). Port of the Claude Design prototype from
   Leaflet to Mapbox GL. Note: Mapbox zoom = Leaflet zoom − 1 (512px tiles), so the numbers
   below are one lower than the handoff's (cluster < 7 → < 6, fly ≥ 14 → ≥ 13, etc.). */
function dslBoot() {
  const root = document.querySelector('[data-dsl]');
  if (!root || root.dataset.dslReady) return;
  root.dataset.dslReady = '1';
  const $ = s => root.querySelector(s);
  const cfg = JSON.parse($('[data-dsl-config]').textContent);
  const Z = { cluster: 6, store: 13, fit: 11, near: 12, far: 6, us: 3, min: 2 };
  const US = [-96, 38.5];
  const PEEK = 212;
  const mq = matchMedia('(max-width: 900px)');
  const isM = () => mq.matches;

  const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const lc = s => String(s ?? '').toLowerCase();
  const ll = s => [s.lng, s.lat];

  let stores = [], regions = [], map, markers = [], meMarker = null, popup = null;
  const state = { region: null, origin: null, active: null };

  // keep the mobile "maps app" card fully visible under the sticky site header
  function syncTop() {
    const nav = document.querySelector('.nav');
    const h = nav && getComputedStyle(nav).position === 'sticky' ? nav.offsetHeight : 0;
    root.style.setProperty('--dsl-top', h + 'px');
  }
  const syncM = () => { syncTop(); if (map) map.resize(); };
  mq.addEventListener('change', syncM);
  window.addEventListener('resize', syncTop);
  syncTop();

  const setSheet = open => $('[data-dsl-sheet]').classList.toggle('open', open);
  const fitPad = () => isM() ? { top: 150, bottom: PEEK + 30, left: 30, right: 30 } : 60;
  const mOffset = () => isM() ? [0, -(PEEK - 140) / 2] : [0, 0];
  function flyCenter(center, zoom, duration = 900) {
    if (!map) return;
    map.flyTo({ center, zoom, duration, offset: mOffset(), essential: true });
  }

  function km(a, b) {
    const R = 6371, toR = x => x * Math.PI / 180;
    const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  const miles = k => k * 0.621371;
  const fmtMi = k => { const m = miles(k); return m < 10 ? m.toFixed(1) + ' mi' : Math.round(m).toLocaleString() + ' mi'; };

  // ---- geocoding (Mapbox, US only). Store lookups are cached in localStorage ----
  async function geocode(q, types) {
    const url = 'https://api.mapbox.com/search/geocode/v6/forward?limit=1&country=us' + (types ? '&types=' + types : '') +
      '&q=' + encodeURIComponent(q) + '&access_token=' + encodeURIComponent(cfg.token);
    const r = await fetch(url);
    if (!r.ok) throw new Error('geocode ' + r.status);
    const f = (await r.json()).features || [];
    if (!f.length) return null;
    const [lng, lat] = f[0].geometry.coordinates;
    return { lat, lng };
  }
  const cacheGet = k => { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } };
  const cacheSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} };
  async function fillMissingCoords(list) {
    await Promise.all(list.filter(s => isNaN(s.lat) || isNaN(s.lng)).map(async s => {
      const q = [s.address, s.city, s.state, s.zip].filter(Boolean).join(', ');
      const key = 'dsl-geo:' + lc(q);
      let p = cacheGet(key);
      if (!p) { try { p = await geocode(q, 'address'); if (p) cacheSet(key, p); } catch (e) {} }
      if (p) { s.lat = p.lat; s.lng = p.lng; }
    }));
    return list.filter(s => !isNaN(s.lat) && !isNaN(s.lng));
  }

  function buildRegions() {
    const out = [];
    stores.forEach(s => {
      let r = out.find(r => km(r.center, s) < cfg.clusterKm);
      if (!r) { r = { stores: [], center: { lat: s.lat, lng: s.lng } }; out.push(r); }
      r.stores.push(s);
      r.center = { lat: r.stores.reduce((a, x) => a + x.lat, 0) / r.stores.length, lng: r.stores.reduce((a, x) => a + x.lng, 0) / r.stores.length };
    });
    out.forEach(r => {
      const counts = {};
      r.stores.forEach(s => { const c = lc(s.city); counts[c] = (counts[c] || 0) + 1; });
      let top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      if (counts['los angeles'] && counts['los angeles'] >= Math.max(...Object.values(counts))) top = 'los angeles';
      r.name = cfg.regionNames[top] || top;
      r.stores.forEach(s => s.region = r);
    });
    return out.sort((a, b) => b.stores.length - a.stores.length);
  }

  function visibleStores() {
    const list = state.region ? state.region.stores.slice() : stores.slice();
    const ref = state.origin || (state.region && state.region.center);
    list.forEach(s => s.d = ref ? km(ref, s) : null);
    if (ref) list.sort((a, b) => a.d - b.d);
    else list.sort((a, b) => b.region.stores.length - a.region.stores.length || lc(a.city).localeCompare(lc(b.city)));
    return list;
  }

  const dirUrl = s => 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent([s.name, s.address, s.city, s.state, s.zip].join(', '));
  const addr = s => esc(s.address) + (s.address_2 ? ', ' + esc(s.address_2) : '') + '<br>' + esc(lc(s.city)) + ', ' + esc(s.state) + ' ' + esc(s.zip);
  const extra = s => [s.phone && `<a href="tel:${esc(s.phone.replace(/[^\d+]/g, ''))}">${esc(s.phone)}</a>`, s.hours && esc(lc(s.hours))].filter(Boolean).join('<br>');
  const ICON_UR = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7M8 7h9v9"></path></svg>';
  const ICON_R = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>';

  function renderPills() {
    $('[data-dsl-pills]').innerHTML =
      regions.map((r, i) => `<button type="button" class="dsl-pill${state.region === r ? ' on' : ''}" data-r="${i}">${esc(r.name)} <span>${r.stores.length}</span></button>`).join('') +
      `<button type="button" class="dsl-pill${!state.region ? ' on' : ''}" data-r="all">everywhere <span>${stores.length}</span></button>`;
  }

  function renderList() {
    const list = visibleStores();
    list.forEach((s, i) => s.n = i + 1);
    const nearest = state.origin ? stores.reduce((m, s) => Math.min(m, km(state.origin, s)), Infinity) : null;
    const far = nearest !== null && miles(nearest) > cfg.fallbackMiles;
    const fallback = far ? `
      <div class="dsl-fallback">
        <div>
          <h4>not near you yet.</h4>
          <p>the closest store is ${fmtMi(nearest)} away. get daps shipped to your door instead.</p>
          <a class="dsl-btn" href="${esc(cfg.shopUrl)}">shop online ${ICON_R}</a>
        </div>
        <img src="${esc(cfg.boxImg)}" alt="" width="84" height="108" loading="lazy">
      </div>` : '';
    $('[data-dsl-list]').innerHTML = fallback + list.map(s => {
      const x = extra(s);
      return `
      <div class="dsl-store${state.active === s ? ' on' : ''}" data-id="${s.id}">
        <div class="dsl-num">${s.n}</div>
        <div>
          <h3>${esc(s.name)}</h3>
          <p>${addr(s)}</p>
          ${x ? `<p class="dsl-extra">${x}</p>` : ''}
          <a class="dsl-dir" href="${dirUrl(s)}" target="_blank" rel="noopener">directions ${ICON_UR}</a>
        </div>
        <div class="dsl-dist">${state.origin ? fmtMi(s.d) : ''}</div>
      </div>`;
    }).join('');

    const where = state.region ? `in <b>${esc(state.region.name)}</b>` : `across <b>${regions.length} ${regions.length === 1 ? 'city' : 'cities'}</b>`;
    const n = `<b>${list.length}</b> ${list.length === 1 ? 'store' : 'stores'}`;
    $('[data-dsl-status]').innerHTML = state.origin ? `${n} ${where}, nearest first` : `${n} ${where}`;
    renderMarkers();
  }

  function el(html, cls) {
    const d = document.createElement('div');
    if (cls) d.className = cls;
    d.innerHTML = html;
    return d;
  }
  const popHtml = s => `<div class="dsl-pop"><h5>${esc(s.name)}</h5><p>${addr(s)}</p><a href="${dirUrl(s)}" target="_blank" rel="noopener">get directions →</a></div>`;
  function openPopup(s) {
    if (!map) return;
    if (popup) popup.remove();
    popup = new mapboxgl.Popup({ offset: 18, maxWidth: '260px', focusAfterOpen: false })
      .setLngLat(ll(s)).setHTML(popHtml(s)).addTo(map);
    popup.on('close', () => { popup = null; });
  }

  function renderMarkers() {
    if (!map) return;
    markers.forEach(m => m.remove());
    markers = [];
    const z = map.getZoom();
    if (z < Z.cluster) {
      regions.forEach(r => {
        const size = Math.min(92, 54 + r.stores.length * 1.6);
        const e = el(`<div><b>${r.stores.length}</b><small>${r.stores.length === 1 ? 'store' : 'stores'}</small></div><div class="dsl-cluster-label">${esc(r.name)}</div>`, 'dsl-cluster');
        e.style.width = e.style.height = size + 'px';
        e.addEventListener('click', ev => { ev.stopPropagation(); selectRegion(r); });
        markers.push(new mapboxgl.Marker({ element: e }).setLngLat(ll(r.center)).addTo(map));
      });
      return;
    }
    const R = isM() ? 30 : 34, groups = [];
    const ordered = stores.slice().sort((a, b) => (a === state.active ? -1 : b === state.active ? 1 : 0));
    ordered.forEach(s => {
      const p = map.project(ll(s));
      const g = cfg.groupPins && z < 15 && groups.find(g => Math.hypot(g.p.x - p.x, g.p.y - p.y) < R);
      if (g && s !== state.active && !g.items.includes(state.active)) g.items.push(s); else groups.push({ p, items: [s] });
    });
    groups.forEach(g => {
      if (g.items.length > 1) {
        const b = new mapboxgl.LngLatBounds();
        g.items.forEach(s => b.extend(ll(s)));
        const e = el(String(g.items.length), 'dsl-group');
        e.addEventListener('click', ev => { ev.stopPropagation(); flyCenter(b.getCenter(), map.getZoom() + 2, 600); });
        markers.push(new mapboxgl.Marker({ element: e }).setLngLat(b.getCenter()).addTo(map));
        return;
      }
      const s = g.items[0];
      const e = el(`<div class="dsl-pin${state.active === s ? ' on' : ''}">${s.n ?? ''}</div>`, 'dsl-pin-wrap');
      e.style.width = e.style.height = (isM() ? 26 : 30) + 'px';
      if (state.active === s) e.style.zIndex = 2;
      e.addEventListener('click', ev => { ev.stopPropagation(); setActive(s, false); });
      markers.push(new mapboxgl.Marker({ element: e }).setLngLat(ll(s)).addTo(map));
    });
  }

  function setActive(s, fly = true) {
    state.active = s;
    if (s.region !== state.region && state.region) { state.region = s.region; renderPills(); }
    renderList();
    if (isM()) setSheet(false);
    if (fly && map) {
      flyCenter(ll(s), Math.max(map.getZoom(), Z.store), 800);
      map.once('moveend', () => { renderMarkers(); openPopup(s); });
    } else openPopup(s);
    if (fly && isM()) {
      const top = $('.dsl-app').getBoundingClientRect().top + window.scrollY - 12 - (parseFloat(root.style.getPropertyValue('--dsl-top')) || 0);
      window.scrollTo({ top, behavior: 'smooth' });
    }
    const row = root.querySelector(`.dsl-store[data-id="${s.id}"]`);
    const list = $('[data-dsl-list]');
    if (row && list.scrollHeight > list.clientHeight) list.scrollTo({ top: row.offsetTop - list.offsetTop - 8, behavior: 'smooth' });
  }

  function fit(points, maxZoom = Z.fit) {
    if (!map) return;
    if (points.length === 1) return flyCenter(points[0], maxZoom);
    const b = new mapboxgl.LngLatBounds();
    points.forEach(p => b.extend(p));
    map.fitBounds(b, { padding: fitPad(), maxZoom, duration: 900 });
  }

  function selectRegion(r) {
    state.region = r; state.active = null;
    if (popup) popup.remove();
    renderPills(); renderList();
    const pts = r.stores.map(ll);
    if (state.origin && km(state.origin, r.center) < cfg.clusterKm) pts.push(ll(state.origin));
    fit(pts);
  }

  function showAll() {
    state.region = null; state.active = null;
    if (popup) popup.remove();
    renderPills(); renderList();
    if (map) map.flyTo({ center: US, zoom: Z.us, duration: 900 });
  }

  function setOrigin(lat, lng) {
    state.origin = { lat, lng };
    state.active = null;
    if (popup) popup.remove();
    if (meMarker) meMarker.remove();
    if (map) meMarker = new mapboxgl.Marker({ element: el('', 'dsl-me') }).setLngLat([lng, lat]).addTo(map);
    if (meMarker) meMarker.getElement().style.zIndex = 3;
    const nearestRegion = regions.slice().sort((a, b) => km(state.origin, a.center) - km(state.origin, b.center))[0];
    const nearest = stores.slice().sort((a, b) => km(state.origin, a) - km(state.origin, b));
    const far = miles(km(state.origin, nearest[0])) > cfg.fallbackMiles;
    state.region = far ? null : nearestRegion;
    renderPills(); renderList();
    if (isM()) setSheet(false);
    if (far) flyCenter([lng, lat], Z.far);
    else fit([[lng, lat], ...nearest.slice(0, 4).map(ll)], Z.near);
  }

  async function search(q) {
    q = q.trim(); if (!q) return;
    const status = $('[data-dsl-status]');
    status.textContent = 'searching…';
    const ql = lc(q);
    const local = stores.filter(s => s.zip === q || lc(s.city) === ql);
    if (local.length) {
      const c = local.reduce((a, s) => ({ lat: a.lat + s.lat / local.length, lng: a.lng + s.lng / local.length }), { lat: 0, lng: 0 });
      return setOrigin(c.lat, c.lng);
    }
    try {
      const p = await geocode(q);
      if (!p) { status.innerHTML = `couldn't find <b>${esc(q)}</b>. try a zip code.`; return; }
      setOrigin(p.lat, p.lng);
    } catch (e) { status.textContent = 'search is unavailable right now. try a city pill instead.'; }
  }

  function locate() {
    const btn = $('[data-dsl-locate]'), status = $('[data-dsl-status]');
    if (!navigator.geolocation) { status.textContent = "location isn't available in this browser."; return; }
    btn.classList.add('busy'); status.textContent = 'finding you…';
    navigator.geolocation.getCurrentPosition(
      p => { btn.classList.remove('busy'); setOrigin(p.coords.latitude, p.coords.longitude); },
      () => { btn.classList.remove('busy'); status.textContent = 'location blocked. search by zip or city instead.'; },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }

  // Recolor Mapbox's dark style to the Daps palette (no Mapbox Studio style needed).
  function paintDaps() {
    const set = (id, prop, val) => { try { map.setPaintProperty(id, prop, val); } catch (e) {} };
    map.getStyle().layers.forEach(l => {
      const id = l.id;
      if (l.type === 'background') set(id, 'background-color', '#061a55');
      else if (l.type === 'fill') {
        if (/water/.test(id)) set(id, 'fill-color', '#001142');
        else if (/land|park|national|pitch|building|wetland|glacier|sand|airport/.test(id)) { set(id, 'fill-color', '#0a1f5e'); set(id, 'fill-opacity', 0.6); }
      } else if (l.type === 'line') {
        if (/water|waterway/.test(id)) set(id, 'line-color', '#001142');
        else if (/admin|boundary/.test(id)) { set(id, 'line-color', '#73E8FF'); set(id, 'line-opacity', 0.25); }
        else if (/road|bridge|tunnel|street|motorway|trunk|primary|secondary|rail|ferry|aeroway/.test(id)) {
          set(id, 'line-color', '#73E8FF');
          set(id, 'line-opacity', /motorway|trunk|primary/.test(id) ? 0.4 : 0.2);
        }
      } else if (l.type === 'symbol') {
        if (/poi|transit|airport|ferry|road-number|road-exit/.test(id)) { try { map.setLayoutProperty(id, 'visibility', 'none'); } catch (e) {} return; }
        set(id, 'text-color', 'rgba(255,255,255,0.7)');
        set(id, 'text-halo-color', 'rgba(0,17,66,0.9)');
        set(id, 'icon-opacity', 0);
      }
    });
  }

  function bindUi() {
    const sheet = $('[data-dsl-sheet]'); let y0 = null, moved = 0;
    sheet.addEventListener('pointerdown', e => {
      if (!isM() || !e.target.closest('.dsl-handle, .dsl-status')) return;
      y0 = e.clientY; moved = 0; sheet.classList.add('drag'); sheet.setPointerCapture(e.pointerId);
    });
    sheet.addEventListener('pointermove', e => {
      if (y0 === null) return; moved = e.clientY - y0;
      const open = sheet.classList.contains('open'), H = sheet.offsetHeight;
      const base = open ? 0 : H - PEEK;
      sheet.style.transform = `translateY(${Math.max(0, Math.min(H - PEEK, base + moved))}px)`;
    });
    const release = () => {
      if (y0 === null) return; y0 = null;
      sheet.classList.remove('drag'); sheet.style.transform = '';
      if (Math.abs(moved) < 8) setSheet(!sheet.classList.contains('open'));
      else setSheet(moved < 0);
    };
    sheet.addEventListener('pointerup', release); sheet.addEventListener('pointercancel', release);

    $('[data-dsl-pills]').addEventListener('click', e => {
      const b = e.target.closest('.dsl-pill'); if (!b) return;
      b.dataset.r === 'all' ? showAll() : selectRegion(regions[+b.dataset.r]);
    });
    $('[data-dsl-list]').addEventListener('click', e => {
      if (e.target.closest('a')) return;
      const row = e.target.closest('.dsl-store'); if (!row) return;
      setActive(stores[+row.dataset.id]);
    });
    $('[data-dsl-search]').addEventListener('submit', e => { e.preventDefault(); search($('[data-dsl-q]').value); });
    $('[data-dsl-locate]').addEventListener('click', locate);
  }

  function setLede() {
    const lead = regions.find(r => r.name === cfg.defaultRegion) || regions[0];
    if (!lead) return;
    const others = regions.filter(r => r !== lead).map(r => r.name);
    $('[data-dsl-lede]').textContent = others.length
      ? `now on shelves across ${lead.name}, plus ${others.slice(0, -1).join(', ')}${others.length > 1 ? ' & ' : ''}${others.slice(-1)}. more stores every month.`
      : `now on shelves across ${lead.name}. more stores every month.`;
  }

  async function init() {
    const status = $('[data-dsl-status]');
    if (!cfg.token || !window.mapboxgl) {
      $('.dsl-map').innerHTML = '<div class="dsl-map-msg">map unavailable — add your Mapbox public token in the theme editor (store locator section).</div>';
    }
    stores = JSON.parse($('[data-dsl-stores]').textContent).map((s, i) => ({ ...s, id: i, lat: parseFloat(s.lat), lng: parseFloat(s.lng) }));
    if (cfg.token) stores = await fillMissingCoords(stores);
    else stores = stores.filter(s => !isNaN(s.lat) && !isNaN(s.lng));
    stores.forEach((s, i) => s.id = i);
    if (!stores.length) { status.textContent = 'no stores yet. check back soon.'; return; }
    regions = buildRegions();
    setLede();
    state.region = regions.find(r => r.name === cfg.defaultRegion) || regions[0];
    if (!cfg.token || !window.mapboxgl) { renderPills(); renderList(); bindUi(); return; }

    mapboxgl.accessToken = cfg.token;
    const b = new mapboxgl.LngLatBounds();
    state.region.stores.forEach(s => b.extend(ll(s)));
    map = new mapboxgl.Map({
      container: $('.dsl-map'), style: cfg.style || 'mapbox://styles/mapbox/dark-v11',
      bounds: b, fitBoundsOptions: { padding: fitPad() }, minZoom: Z.min,
      attributionControl: false, dragRotate: false, pitchWithRotate: false, touchPitch: false, projection: 'mercator'
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.on('style.load', () => { if (!cfg.style) paintDaps(); });
    map.on('zoomend', renderMarkers);
    map.on('load', () => { renderPills(); renderList(); });
    bindUi();
  }
  init();
}
function dslStart() {
  // mapbox-gl.js is deferred like this file, but wait for it in case it arrives late
  if (window.mapboxgl || !document.querySelector('[data-dsl]')) return dslBoot();
  let tries = 0;
  const t = setInterval(() => { if (window.mapboxgl || ++tries > 50) { clearInterval(t); dslBoot(); } }, 100);
}
dslStart();
// theme editor re-renders the section on every setting change
document.addEventListener('shopify:section:load', e => { if (e.target.querySelector('[data-dsl]')) dslStart(); });
